/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './DivColumn';
import { helpRenders } from './DivColumn_Help';

const DivColumnManager: ComponentManagerFn<'DivColumn'> = (
  platform: Platform,
): ComponentManager<'DivColumn'> => {
  return {
    ...baseComponentManager<'DivColumn'>(platform, 'DivColumn'),
    type: 'DivColumn',
    name: 'DIV Column',
    isChildAllowed: (page, item, childType) =>
      (['Box', 'RowPanel', 'TriggerOnCellClick'] as ItemType[]).includes(
        childType,
      ),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        required: false,
      },
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'viewAttribute':
        case 'insertable':
        case 'updatable':
        case 'filterable':
        case 'sortable':
        case 'required':
        case 'toolTip':
        case 'toolTipPosition':
        case 'toolTipMaxWidth':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    canHaveChildren: () => true,
    icon: { iconName: 'html5', iconType: 'brands' },
    description: `The Div Column is a layout-oriented data grid column designed to contain Box components inside each cell.

      Each Box acts as a flexible layout container, allowing you to arrange visual elements such as Typography, Buttons, or Icons — enabling the creation of custom, interactive cell content.
      
      You can also add a TriggerOnCellClick to handle click-based events within the cell — ideal for building dynamic layouts that combine visual presentation with interactivity.
      
      This column type is especially useful when you need a grid cell that behaves like a mini component container, providing consistent and responsive layout control.`,
    helpRenders,
  };
};

export default DivColumnManager;
